import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo } from 'react';
import { db } from '../db/indexedDb';
import { usePlayer } from '../hooks/usePlayer';
import { useLiveQuery } from 'dexie-react-hooks';
import { formatDuration } from '../lib/audio';
import type { Track } from '../types';
import { Artwork } from './Artwork';

const EMPTY_TRACKS: Track[] = [];

function QueueRow({
  id,
  title,
  subtitle,
  duration,
  onRemove
}: {
  id: string;
  title: string;
  subtitle: string;
  duration: number;
  onRemove: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className="flex items-center gap-3 rounded-2xl border border-white/10 bg-white/5 p-3"
    >
      <button type="button" className="pill cursor-grab" aria-label="Reorder queued track" {...attributes} {...listeners}>
        Move
      </button>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{title}</p>
        <p className="truncate text-sm text-foreground/65">
          {subtitle} • {formatDuration(duration)}
        </p>
      </div>
      <button type="button" onClick={onRemove} className="pill">
        Remove
      </button>
    </li>
  );
}

export function QueueDrawer({ open }: { open: boolean }) {
  const { queue, currentIndex, currentTrack, setQueueOpen, reorderQueue, removeFromQueue, clearQueue } =
    usePlayer();
  const sensors = useSensors(useSensor(PointerSensor));
  const queuedTracksQuery = useLiveQuery(async () => {
    const tracks = await Promise.all(queue.map((id) => db.tracks.get(id)));
    return tracks.filter((track): track is Track => Boolean(track));
  }, [queue], EMPTY_TRACKS);
  const queuedTracks = queuedTracksQuery ?? EMPTY_TRACKS;

  const items = useMemo(() => queuedTracks.map((track) => track.id), [queuedTracks]);

  return (
    <aside
      aria-hidden={!open}
      className={`fixed inset-y-0 right-0 z-50 w-full max-w-md transform border-l border-white/10 bg-background/95 p-4 backdrop-blur-2xl transition ${
        open ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      <div className="flex h-full flex-col">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-accent">Queue</p>
            <h2 className="mt-2 font-display text-3xl font-semibold">Up next</h2>
            <p className="mt-2 text-sm text-foreground/70">
              Now playing: {currentTrack?.title ?? 'No active track'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setQueueOpen(false)}
            className="pill"
            aria-label={open ? 'Collapse queue panel' : 'Expand queue panel'}
          >
            Close
          </button>
        </div>

        {currentTrack ? (
          <div className="mt-6 flex items-center gap-4 rounded-3xl border border-white/10 bg-white/5 p-4">
            <Artwork track={currentTrack} size="md" />
            <div className="min-w-0">
              <p className="truncate font-semibold">{currentTrack.title}</p>
              <p className="truncate text-sm text-foreground/65">
                {currentTrack.artist} • {currentTrack.album}
              </p>
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-foreground/65">
            {Math.max(queue.length - (currentIndex + 1), 0)} tracks upcoming
          </p>
          <button type="button" onClick={clearQueue} className="pill">
            Clear
          </button>
        </div>

        <div className="mt-4 flex-1 overflow-y-auto pr-1">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => {
              const { active, over } = event;
              if (!over || active.id === over.id) return;
              const oldIndex = items.indexOf(String(active.id));
              const newIndex = items.indexOf(String(over.id));
              if (oldIndex < 0 || newIndex < 0) return;
              reorderQueue(oldIndex, newIndex);
            }}
          >
            <SortableContext items={items} strategy={verticalListSortingStrategy}>
              <ul className="space-y-3">
                {queuedTracks.map((track) => (
                  <QueueRow
                    key={track.id}
                    id={track.id}
                    title={track.title}
                    subtitle={`${track.artist} • ${track.album}`}
                    duration={track.duration}
                    onRemove={() => removeFromQueue(track.id)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        </div>
      </div>
    </aside>
  );
}
